import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import {
  MessageCircle, Send, Square, Plus, Pencil, Trash2, Loader2, GraduationCap, History,
  TrendingUp, Mic, MicOff, Volume2, ChevronDown, Snowflake, ThermometerSun, Flame,
  Smile, Meh, Angry, PhoneOff, User as UserIcon,
  Drama, Briefcase, Zap, Search, Building2, Home, Building, DollarSign, Scale, Wallet,
  Cpu, ShieldQuestion, Sparkles, Target, Globe2, Crown, Gauge, Swords, Megaphone, BadgeCheck,
  CheckCircle2, XCircle, Activity, MessageSquare, HelpCircle, Presentation,
} from "lucide-react";

// lucide component lookup for icon names sent by the backend traits registry.
const ICON_MAP = {
  Drama, Smile, Briefcase, Zap, Search, Building2, Home, Building, DollarSign, Scale,
  Wallet, Cpu, ShieldQuestion, Sparkles, Meh, Target, Globe2, Crown, Gauge, Swords,
  Megaphone, BadgeCheck, TrendingUp,
};

// Random Turkish persona name (so admins don't have to invent one).
const TR_FIRST = ["Muhammed", "Serkan", "Hakan", "Emre", "Mehmet", "Ahmet", "Mustafa",
  "Ali", "Kaan", "Burak", "Tolga", "Cem", "Onur", "Volkan", "Yusuf", "Erdem", "Selim", "Barış"];
const TR_LAST = ["Yılmaz", "Demir", "Şahin", "Çelik", "Aydın", "Yıldız", "Öztürk", "Arslan",
  "Doğan", "Kaya", "Kılıç", "Aksoy", "Polat", "Korkmaz", "Erdoğan", "Güneş", "Taş", "Bulut"];
function randomTurkishName() {
  const f = TR_FIRST[Math.floor((Date.now() + Math.random() * 1e6) % TR_FIRST.length)];
  const l = TR_LAST[Math.floor((Date.now() * 7 + Math.random() * 1e6) % TR_LAST.length)];
  return `${f} ${l}`;
}
import usePageTitle from "../hooks/usePageTitle";
import { useAuth } from "../context/AuthContext";
import {
  createSTT, createTTS, sttSupported, ttsSupported, splitSentences,
  createRecorder, createAudioPlayer, recorderSupported,
} from "../lib/voiceProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback voice/BCP-47 map; backend /roleplay/scenarios also returns the
// canonical list (used for the dropdown). Turkish is the real-work default.
const LANG_FALLBACK = [
  { code: "tr", name: "Turkish (Türkçe)", voice: "tr-TR" },
  { code: "ru", name: "Russian (Русский)", voice: "ru-RU" },
  { code: "en", name: "English", voice: "en-US" },
  { code: "uk", name: "Ukrainian (Українська)", voice: "uk-UA" },
];

function scoreColor(v) {
  if (v >= 75) return "text-emerald-600";
  if (v >= 50) return "text-amber-600";
  return "text-red-600";
}

// Initials avatar with a stable color derived from the name (no photos needed).
const AVATAR_COLORS = [
  "bg-orange-500", "bg-sky-500", "bg-emerald-500", "bg-violet-500",
  "bg-rose-500", "bg-amber-500", "bg-teal-500", "bg-indigo-500",
];
function PersonaAvatar({ name, size = "h-11 w-11" }) {
  const clean = (name || "?").replace(/^Persona\s+[A-Z]\s*[—-]\s*/i, "").trim() || "?";
  const initials = clean.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  let h = 0;
  for (let i = 0; i < clean.length; i++) h = (h * 31 + clean.charCodeAt(i)) >>> 0;
  const color = AVATAR_COLORS[h % AVATAR_COLORS.length];
  return (
    <div className={`${size} ${color} rounded-full text-zinc-50 flex items-center justify-center font-semibold shrink-0 text-sm`}>
      {initials}
    </div>
  );
}

// Shared SSE reader — streams the buyer reply for both text and voice modes.
// Calls onDelta(text) per chunk and onSentence(text) when a full sentence is
// ready (voice feeds that straight into TTS so the buyer starts talking early).
async function streamBuyerReply(sessionId, text, { signal, onDelta, onSentence, onError }) {
  const resp = await fetch(`${BACKEND_URL}/api/roleplay/sessions/${sessionId}/message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ text }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let spoken = ""; // text already handed to onSentence
  let full = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop(); // the last chunk may be incomplete
    for (const ev of events) {
      const line = ev.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      let payload;
      try { payload = JSON.parse(line.slice(6)); } catch { continue; }
      if (payload.delta) {
        full += payload.delta;
        if (onDelta) onDelta(payload.delta);
        if (onSentence) {
          const [sentences, rest] = splitSentences(full.slice(spoken.length));
          if (sentences.length) {
            sentences.forEach((s) => onSentence(s));
            spoken = full.slice(0, full.length - rest.length);
          }
        }
      } else if (payload.error && onError) {
        onError(payload.error);
      }
    }
  }
  // flush trailing partial sentence to TTS
  if (onSentence) {
    const remainder = full.slice(spoken.length).trim();
    if (remainder) onSentence(remainder);
  }
  return full.trim();
}

// Leadership "Just me / All reps" switch (oversight of other reps' training).
function ViewToggle({ viewAll, setViewAll }) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-xs text-muted-foreground mr-1">View:</span>
      <Button size="sm" variant={!viewAll ? "default" : "outline"} className="h-8 px-2.5 text-xs" onClick={() => setViewAll(false)}>Just me</Button>
      <Button size="sm" variant={viewAll ? "default" : "outline"} className="h-8 px-2.5 text-xs" onClick={() => setViewAll(true)}>All reps</Button>
    </div>
  );
}

function MetricCard({ label, value, hint }) {
  return (
    <div className="border rounded-lg p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function ScoreCard({ score }) {
  if (!score) return null;
  const a = score.analytics || {};
  const byCat = {};
  (score.scorecard || []).forEach((it) => { (byCat[it.category] = byCat[it.category] || []).push(it); });
  const fmtDur = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String((s || 0) % 60).padStart(2, "0")}`;
  return (
    <Card className="border-2">
      <CardContent className="pt-4 space-y-4">
        {/* header */}
        <div className="flex items-start gap-4">
          <div className="text-center shrink-0">
            <div className={`text-5xl font-bold tabular-nums ${scoreColor(score.overall)}`}>{score.overall}</div>
            <div className="text-[11px] text-muted-foreground">overall · 0–100</div>
          </div>
          <div className="min-w-0">
            {score.summary && <p className="text-sm">{score.summary}</p>}
            {score.model && <div className="text-[11px] text-muted-foreground mt-1">{score.model}</div>}
          </div>
        </div>

        <Tabs defaultValue="feedback">
          <TabsList>
            <TabsTrigger value="feedback"><BadgeCheck className="h-4 w-4 mr-1" />Feedback</TabsTrigger>
            <TabsTrigger value="analytics"><Activity className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="objections"><MessageSquare className="h-4 w-4 mr-1" />Objections</TabsTrigger>
          </TabsList>

          {/* ---- Feedback ---- */}
          <TabsContent value="feedback" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-semibold text-emerald-600 mb-1">What went well</div>
                <ul className="text-sm space-y-1 list-disc pl-4">
                  {(score.did_well || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold text-red-600 mb-1">To improve</div>
                <ul className="text-sm space-y-1 list-disc pl-4">
                  {(score.did_wrong || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            </div>
            {score.top_recommendation && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-sm">
                <span className="font-semibold">Top recommendation: </span>{score.top_recommendation}
              </div>
            )}
            {/* scorecard */}
            {Object.keys(byCat).length > 0 && (
              <div className="space-y-3">
                <div className="text-sm font-semibold">Scorecard</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(byCat).map(([cat, items]) => (
                    <div key={cat} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{cat}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {items.filter((x) => x.passed).length}/{items.length}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {items.map((it, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm" title={it.note}>
                            {it.passed
                              ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                              : <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
                            <span className={it.passed ? "" : "text-muted-foreground"}>{it.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* rubric criteria */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(score.criteria || []).map((c) => (
                <div key={c.key} className="border rounded-md p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{c.label}</span>
                    <span className={`font-bold tabular-nums ${scoreColor(c.score * 10)}`}>{c.score}/10</span>
                  </div>
                  {c.note && <div className="text-xs text-muted-foreground mt-1">{c.note}</div>}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ---- Analytics ---- */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Talk / listen" value={`${a.talk_listen_pct ?? 0}%`} hint="your share of words" />
              <MetricCard label="Talk speed" value={`${a.talk_speed_wpm ?? 0} wpm`} hint="approx." />
              <MetricCard label="Filler words" value={a.filler_count ?? 0} hint="um / yani / ну…" />
              <MetricCard label="Longest monologue" value={`${a.longest_monologue_words ?? 0}w`} hint="single turn" />
              <MetricCard label="Call length" value={fmtDur(a.duration_sec)} />
              <MetricCard label="Your words" value={a.rep_words ?? 0} />
              <MetricCard label="Buyer words" value={a.buyer_words ?? 0} />
              <MetricCard label="Your turns" value={a.rep_turns ?? 0} />
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Talk speed is approximate (no per-utterance audio timing yet). Aim for a balanced talk/listen
              ratio and short monologues.
            </p>
          </TabsContent>

          {/* ---- Objections ---- */}
          <TabsContent value="objections" className="space-y-4">
            <div>
              <div className="text-sm font-semibold mb-1.5">Buyer objections &amp; how you handled them</div>
              {(score.objections || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No objections identified.</div>
              ) : (
                <div className="space-y-2">
                  {score.objections.map((o, i) => (
                    <div key={i} className="border rounded-md p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium">{o.objection}</span>
                        <Badge variant="outline" className={`shrink-0 text-[10px] ${
                          o.handled === "yes" ? "text-emerald-600" : o.handled === "partly" ? "text-amber-600" : "text-red-600"
                        }`}>{o.handled}</Badge>
                      </div>
                      {o.note && <div className="text-xs text-muted-foreground mt-1">{o.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                <HelpCircle className="h-4 w-4" /> Discovery questions you asked
              </div>
              {(score.questions_asked || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No discovery questions identified.</div>
              ) : (
                <ul className="text-sm space-y-1 list-disc pl-4">
                  {score.questions_asked.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Chat transcript bubbles, shared by text and voice modes.
function Transcript({ messages, endRef }) {
  return (
    <div className="space-y-3 min-h-[260px] max-h-[50vh] overflow-y-auto pr-1">
      {messages.map((m, i) => (
        <div key={i} className={`flex ${m.role === "rep" ? "justify-end" : "justify-start"}`}>
          <div className={`rounded-lg px-3 py-2 max-w-[80%] whitespace-pre-wrap text-sm ${
            m.role === "rep" ? "bg-orange-500 text-white" : "bg-muted"
          }`}>
            {m.text || <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

// Fetch ElevenLabs audio (auth header) → Blob. Optional voiceId/model for preview.
async function fetchTTS(text, language, { voiceId, model, signal } = {}) {
  const resp = await fetch(`${BACKEND_URL}/api/roleplay/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ text, language, voice_id: voiceId, model }),
    signal,
  });
  if (!resp.ok) throw new Error(`TTS HTTP ${resp.status}`);
  return resp.blob();
}

// Call type & difficulty metadata (label + icon + color) — shown as badges and
// chosen in the scenario editor. Keys match the backend (cold/warm/hot, easy/medium/hard).
const CALL_TYPE_META = {
  cold: { label: "Cold call", icon: Snowflake, cls: "text-sky-600" },
  warm: { label: "Warm lead", icon: ThermometerSun, cls: "text-amber-600" },
  hot: { label: "Hot lead", icon: Flame, cls: "text-red-600" },
  demo: { label: "Demo / Zoom", icon: Presentation, cls: "text-violet-600" },
};
const DIFFICULTY_META = {
  easy: { label: "Nice", icon: Smile, cls: "text-emerald-600" },
  medium: { label: "Balanced", icon: Meh, cls: "text-amber-600" },
  hard: { label: "Tough", icon: Angry, cls: "text-red-600" },
};

function MetaBadge({ meta }) {
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className="text-xs font-medium px-2 py-0.5 gap-1.5">
      <Icon className={`h-4 w-4 ${meta.cls}`} /> {meta.label}
    </Badge>
  );
}

// One trait category as a row of selectable icon chips (persona builder).
function TraitRow({ category, value, onChange }) {
  const CatIcon = ICON_MAP[category.icon] || Smile;
  return (
    <div>
      <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
        <CatIcon className="h-3.5 w-3.5" /> {category.label}
      </Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {category.options.map((o) => {
          const Icon = ICON_MAP[o.icon] || Smile;
          const active = value === o.value;
          return (
            <button
              type="button"
              key={o.value}
              onClick={() => onChange(active ? undefined : o.value)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs transition ${
                active
                  ? "border-orange-500 bg-orange-500/10 text-orange-600 font-medium"
                  : "border-input text-muted-foreground hover:bg-muted"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============ Voice picker (admin): choose model + voice, preview, save ============
function VoicePicker() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [sample, setSample] = useState("Merhaba, biz öyle lüks projeler geliştiren bir firma değiliz. Bizim daireler çok daha uygun fiyatlı.");
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/roleplay/voice/voices").then((r) => {
      setData(r.data);
      setModel(r.data.current?.model || r.data.models?.[0]?.id || "");
      setVoiceId(r.data.current?.voice_id || r.data.voices?.[0]?.voice_id || "");
    }).catch(() => setData({ enabled: false, voices: [], models: [] }));
  }, []);

  const preview = async () => {
    if (!sample.trim()) return;
    setPlaying(true);
    try {
      const blob = await fetchTTS(sample, "tr", { voiceId, model });
      if (audioRef.current) { audioRef.current.pause(); }
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { URL.revokeObjectURL(url); setPlaying(false); };
      a.onerror = () => { URL.revokeObjectURL(url); setPlaying(false); };
      await a.play();
    } catch (e) {
      setPlaying(false);
      toast.error(e.message || "Preview failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/roleplay/voice/settings", { voice_id: voiceId, model });
      toast.success("Voice saved — buyers will use it now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return null;
  if (!data.enabled) {
    return (
      <div className="text-xs text-muted-foreground border rounded-md px-3 py-2">
        Buyer voice uses the browser fallback — set <code>ELEVENLABS_API_KEY</code> for natural voices.
      </div>
    );
  }
  const voiceName = (data.voices.find((v) => v.voice_id === voiceId) || {}).name || "—";
  const modelShort = (model || "").replace("eleven_", "").replace(/_/g, " ");
  return (
    <Card>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/40 rounded-lg">
        <span className="flex items-center gap-2 text-sm font-medium min-w-0">
          <Volume2 className="h-4 w-4 shrink-0" /> Buyer voice
          <span className="text-xs text-muted-foreground font-normal truncate">· {voiceName} · {modelShort}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <CardContent className="pt-0 space-y-2.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div className="min-w-0">
              <Label className="text-xs text-muted-foreground">Model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger className="w-full h-9 overflow-hidden [&>span]:min-w-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(data.models || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0">
              <Label className="text-xs text-muted-foreground">Voice</Label>
              <Select value={voiceId} onValueChange={setVoiceId}>
                <SelectTrigger className="w-full h-9 overflow-hidden [&>span]:min-w-0"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(data.voices || []).map((v) => (
                    <SelectItem key={v.voice_id} value={v.voice_id}>
                      {v.name}{v.desc ? ` — ${v.desc}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Input value={sample} onChange={(e) => setSample(e.target.value)}
                   className="h-9 flex-1" placeholder="Sample text" />
            <Button variant="secondary" size="sm" className="h-9 shrink-0" onClick={preview} disabled={playing}>
              {playing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            <Button size="sm" className="h-9 shrink-0" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ============ Voice call panel (Phase 2) ============
// STT: "deepgram" (server, MediaRecorder push-to-talk) or "browser" (Web Speech).
// TTS: "elevenlabs" (server mp3) or "browser" (speechSynthesis).
// Falls back per-half so it works with only some keys present.
function VoicePanel({ session, setMessages, voiceLang = "tr-TR", sttMode = "browser", ttsMode = "browser", onStatus }) {
  const [status, setStatus] = useState("idle"); // idle | listening | thinking | speaking
  const [interim, setInterim] = useState("");
  const [auto, setAuto] = useState(true);
  const sttRef = useRef(null);       // browser Web Speech
  const ttsRef = useRef(null);       // browser speechSynthesis
  const recorderRef = useRef(null);  // server STT recorder
  const playerRef = useRef(null);    // server TTS audio player
  const audioQueue = useRef([]);     // pending sentences for server TTS
  const queueBusy = useRef(false);
  const activeAudio = useRef(0);     // sentences not yet finished speaking
  const streamDone = useRef(false);
  const autoRef = useRef(auto);
  const abortRef = useRef(null);
  const langCode = (session.language || "tr");

  const browserSttOk = sttSupported();
  const deepgramOk = sttMode === "deepgram" && recorderSupported();
  // Default to DEEPGRAM when available — better recognition (esp. tr/ru). Browser
  // Web Speech is the fallback / a toggle (live captions, but lower accuracy).
  const [engine, setEngine] = useState(deepgramOk ? "deepgram" : (browserSttOk ? "browser" : null));
  const usableStt = engine || (browserSttOk ? "browser" : (deepgramOk ? "deepgram" : null));
  const serverTtsOk = ttsMode === "elevenlabs";
  const usableTts = serverTtsOk ? "elevenlabs" : (ttsSupported() ? "browser" : null);
  const canVoice = !!usableStt && !!usableTts;

  useEffect(() => { autoRef.current = auto; }, [auto]);

  useEffect(() => {
    sttRef.current = createSTT(voiceLang);
    ttsRef.current = createTTS(voiceLang);
    recorderRef.current = createRecorder();
    playerRef.current = createAudioPlayer();
    return () => {
      sttRef.current?.stop();
      ttsRef.current?.cancel();
      recorderRef.current?.abort();
      playerRef.current?.cancel();
      if (abortRef.current) abortRef.current.abort();
    };
    // voiceLang fixed for the session's lifetime — create providers once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-listen restart goes through a ref so maybeResume (memoized) always
  // calls the latest startListening, not a stale render-0 closure.
  const startListeningRef = useRef(null);
  const maybeResume = useCallback(() => {
    if (streamDone.current && activeAudio.current === 0 && !queueBusy.current) {
      setStatus("idle");
      if (autoRef.current) setTimeout(() => startListeningRef.current && startListeningRef.current(), 400);
    }
  }, []);

  // Server TTS: PIPELINE — fetch each sentence's audio concurrently as it
  // arrives, play in order. While clip N plays, N+1/N+2 are already being
  // synthesized → near-zero gaps between sentences (kills the choppy feel).
  const drainPlayer = useCallback(async () => {
    if (queueBusy.current) return;
    queueBusy.current = true;
    while (audioQueue.current.length) {
      const promise = audioQueue.current.shift(); // a fetchTTS() promise
      let blob = null;
      try { blob = await promise; } catch { blob = null; }
      if (blob) await new Promise((resolve) => playerRef.current.speak(blob, resolve));
      activeAudio.current -= 1;
    }
    queueBusy.current = false;
    // queue empty → now safe to resume (this was the missing call that hung
    // the auto-dialog). Re-drain if a clip arrived during the gap.
    if (audioQueue.current.length) drainPlayer();
    else maybeResume();
  }, [maybeResume]);

  const speakSentence = useCallback((sentence) => {
    activeAudio.current += 1;
    if (usableTts === "elevenlabs") {
      // kick off synthesis immediately (concurrent), queue the promise in order
      audioQueue.current.push(fetchTTS(sentence, langCode).catch(() => null));
      drainPlayer();
    } else {
      ttsRef.current.speak(sentence, () => {
        activeAudio.current -= 1;
        maybeResume();
      });
    }
  }, [usableTts, langCode, drainPlayer, maybeResume]);

  const handleUtterance = useCallback(async (text) => {
    if (!text || !text.trim()) { setStatus("idle"); return; }
    setInterim("");
    setStatus("thinking");
    setMessages((m) => [...m, { role: "rep", text }, { role: "buyer", text: "" }]);
    streamDone.current = false;
    activeAudio.current = 0;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamBuyerReply(session.id, text, {
        signal: controller.signal,
        onDelta: (delta) => {
          setStatus("speaking");
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = { ...next[next.length - 1], text: next[next.length - 1].text + delta };
            return next;
          });
        },
        onSentence: (sentence) => speakSentence(sentence),
        onError: (e) => toast.error(e),
      });
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Voice stream failed");
      setMessages((m) => (m.length && m[m.length - 1].role === "buyer" && !m[m.length - 1].text
        ? m.slice(0, -1) : m));
    } finally {
      streamDone.current = true;
      abortRef.current = null;
      maybeResume();
    }
  }, [session.id, setMessages, speakSentence, maybeResume]);

  // Server STT: stop recording → transcribe → handleUtterance.
  const finishingRef = useRef(false);
  const finishRecording = useCallback(async () => {
    if (finishingRef.current) return; // guard: VAD + manual stop can both fire
    finishingRef.current = true;
    setStatus("thinking");
    let blob;
    try { blob = await recorderRef.current.stop(); } catch { blob = null; }
    finishingRef.current = false;
    if (!blob || !blob.size) { setStatus("idle"); return; }
    try {
      const form = new FormData();
      form.append("audio", blob, "utt.webm");
      form.append("language", langCode);
      const resp = await fetch(`${BACKEND_URL}/api/roleplay/sessions/${session.id}/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const { text } = await resp.json();
      if (text && text.trim()) handleUtterance(text);
      else { setStatus("idle"); toast.message("Bir şey duyamadım — tekrar dene"); }
    } catch (e) {
      setStatus("idle");
      toast.error(e.message || "Transcription failed");
    }
  }, [session.id, langCode, handleUtterance]);

  const startListening = useCallback(() => {
    if (!canVoice) return;
    setInterim("");
    if (usableStt === "deepgram") {
      setStatus("listening");
      recorderRef.current.start({ onSpeechEnd: () => finishRecording() }).catch((e) => {
        setStatus("idle");
        toast.error(`Mic: ${e.message || e}`);
      });
    } else {
      ttsRef.current.warmVoices();
      setStatus("listening");
      sttRef.current.start({
        onInterim: (t) => setInterim(t),
        onFinal: (t) => handleUtterance(t),
        onError: (err) => { if (err !== "no-speech" && err !== "aborted") toast.error(`Mic: ${err}`); },
        onEnd: (t) => { if (!t) setStatus((s) => (s === "listening" ? "idle" : s)); },
      });
    }
  }, [canVoice, usableStt, handleUtterance, finishRecording]);

  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

  // Lift status to the parent (call screen highlights who's speaking).
  useEffect(() => { if (onStatus) onStatus(status); }, [status, onStatus]);

  // Auto-open the call: start listening shortly after mount (the "Voice call"
  // click is a fresh user gesture, so getUserMedia/AudioContext are allowed).
  useEffect(() => {
    const t = setTimeout(() => { startListeningRef.current && startListeningRef.current(); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMicButton = () => {
    if (status === "listening") {
      if (usableStt === "deepgram") finishRecording();
      else sttRef.current.stop();
    } else {
      startListening();
    }
  };

  const stopAll = useCallback(() => {
    setAuto(false);
    sttRef.current?.stop();
    ttsRef.current?.cancel();
    recorderRef.current?.abort();
    playerRef.current?.cancel();
    audioQueue.current = [];
    queueBusy.current = false;
    activeAudio.current = 0;
    if (abortRef.current) abortRef.current.abort();
    setStatus("idle");
    setInterim("");
  }, []);

  if (!canVoice) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm">
        Voice isn't available in this browser. The browser fallback (Web Speech) works only in
        <b> Chrome</b> / <b>Edge</b>. Connect Deepgram + ElevenLabs keys for cross-browser voice,
        or switch to text mode.
      </div>
    );
  }

  const statusLabel = {
    idle: usableStt === "deepgram" ? "Ready — press Talk and just speak (auto-sends on pause)" : "Ready — press Talk and speak",
    listening: usableStt === "deepgram" ? "🎙️ Listening... pause when done (or press to send)" : "🎙️ Listening...",
    thinking: "💭 Buyer is thinking...",
    speaking: "🔊 Buyer is speaking...",
  }[status];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center gap-3">
        <Button
          size="lg"
          onClick={onMicButton}
          disabled={status === "thinking" || status === "speaking"}
          variant={status === "listening" ? "destructive" : "default"}
          className="rounded-full h-16 w-16 p-0"
        >
          {status === "listening" ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
        </Button>
        <Button variant="outline" size="sm" onClick={stopAll}>
          <Square className="h-4 w-4 mr-1" /> Stop
        </Button>
        <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
          Auto-listen
        </label>
        {browserSttOk && deepgramOk && (
          <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={engine === "deepgram"}
              disabled={status !== "idle"}
              onChange={(e) => setEngine(e.target.checked ? "deepgram" : "browser")}
            />
            Deepgram (accurate)
          </label>
        )}
      </div>
      <div className="text-center text-sm font-medium flex items-center justify-center gap-2 min-h-[24px]">
        {(status === "thinking" || status === "speaking") && <Volume2 className="h-4 w-4" />}
        {statusLabel}
      </div>
      {interim && (
        <div className="text-center text-sm text-muted-foreground italic">"{interim}"</div>
      )}
      <div className="text-center text-[11px] text-muted-foreground">
        STT: {usableStt === "deepgram" ? "Deepgram" : "browser"} · TTS: {usableTts === "elevenlabs" ? "ElevenLabs" : "browser"}
      </div>
    </div>
  );
}

export default function RoleplayPage() {
  usePageTitle("Roleplay Trainer");
  const { user } = useAuth();
  // Leadership oversight: admin + sales_director can view every rep's sessions & progress.
  const canViewAll = ["admin", "sales_director"].includes(user?.role);
  const [viewAll, setViewAll] = useState(false);
  const [repFilter, setRepFilter] = useState("all");
  const [tab, setTab] = useState("training");
  const [scenarios, setScenarios] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [languages, setLanguages] = useState(LANG_FALLBACK);
  const [cardLang, setCardLang] = useState({}); // per-scenario language: {scenarioId: code}
  const getLang = (id) => cardLang[id] || "tr";
  const [voiceCaps, setVoiceCaps] = useState({ stt: "browser", tts: "browser" });
  const [traitDefs, setTraitDefs] = useState(null); // persona-builder registry
  const [session, setSession] = useState(null); // active session doc
  const [activeScenario, setActiveScenario] = useState(null); // persona meta for the call screen
  const [voiceStatus, setVoiceStatus] = useState("idle"); // idle|listening|thinking|speaking (lifted from VoicePanel)
  const [elapsed, setElapsed] = useState(0); // call timer (seconds)
  const [messages, setMessages] = useState([]); // {role, text}
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [score, setScore] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [viewSession, setViewSession] = useState(null); // full past session in dialog
  const [editScenario, setEditScenario] = useState(null); // scenario being edited/created
  const chatEndRef = useRef(null);
  const abortRef = useRef(null);

  const loadScenarios = useCallback(() => {
    api.get("/roleplay/scenarios").then((r) => {
      setScenarios(r.data.scenarios || []);
      setCanManage(!!r.data.can_manage);
      if (r.data.languages?.length) setLanguages(r.data.languages);
    }).catch(() => toast.error("Failed to load scenarios"));
  }, []);

  const loadHistory = useCallback(() => {
    const q = (canViewAll && viewAll) ? "?all=1" : "";
    api.get(`/roleplay/sessions${q}`).then((r) => setPastSessions(r.data.sessions || [])).catch(() => {});
    api.get(`/roleplay/progress${q}`).then((r) => setProgress(r.data)).catch(() => {});
  }, [canViewAll, viewAll]);

  useEffect(() => {
    api.get("/roleplay/voice/capabilities")
      .then((r) => setVoiceCaps(r.data))
      .catch(() => setVoiceCaps({ stt: "browser", tts: "browser" }));
    api.get("/roleplay/traits").then((r) => setTraitDefs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadScenarios(); loadHistory(); }, [loadScenarios, loadHistory]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, score]);

  // Call timer for the voice call screen.
  useEffect(() => {
    if (!session || session.mode !== "voice" || score) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [session, score]);

  const startSession = async (scenario, mode = "text") => {
    try {
      const r = await api.post("/roleplay/sessions", { scenario_id: scenario.id, mode, language: getLang(scenario.id) });
      setSession(r.data);
      setActiveScenario(scenario);
      setMessages([]);
      setScore(null);
      setDraft("");
      setVoiceStatus("idle");
      setElapsed(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start session");
    }
  };

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || !session || streaming) return;
    setDraft("");
    setMessages((m) => [...m, { role: "rep", text }, { role: "buyer", text: "" }]);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamBuyerReply(session.id, text, {
        signal: controller.signal,
        onDelta: (delta) => {
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = { ...next[next.length - 1], text: next[next.length - 1].text + delta };
            return next;
          });
        },
        onError: (e) => toast.error(e),
      });
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Stream failed");
      setMessages((m) => (m.length && m[m.length - 1].role === "buyer" && !m[m.length - 1].text
        ? m.slice(0, -1) : m));
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const finishSession = async () => {
    if (!session) return;
    if (abortRef.current) abortRef.current.abort();
    window.speechSynthesis?.cancel();
    setScoring(true);
    try {
      const r = await api.post(`/roleplay/sessions/${session.id}/finish`);
      setScore(r.data.score);
      setSession((s) => ({ ...s, status: "scored" }));
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoring failed");
    } finally {
      setScoring(false);
    }
  };

  const exitSession = () => {
    if (abortRef.current) abortRef.current.abort();
    window.speechSynthesis?.cancel();
    setSession(null);
    setMessages([]);
    setScore(null);
    loadHistory();
  };

  const openPastSession = async (id) => {
    try {
      const r = await api.get(`/roleplay/sessions/${id}`);
      setViewSession(r.data);
    } catch { toast.error("Failed to load session"); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/roleplay/sessions/${id}`);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const saveScenario = async () => {
    const s = { ...editScenario };
    if (!s.name) s.name = s.persona_name || "Buyer"; // card title auto = persona name
    const hasTraits = s?.traits && Object.values(s.traits).some(Boolean);
    if (!(s.buyer_prompt || "").trim() && !hasTraits) {
      toast.error("Pick at least a few persona traits (or write a free-hand prompt)");
      return;
    }
    try {
      if (s.id) await api.put(`/roleplay/scenarios/${s.id}`, s);
      else await api.post("/roleplay/scenarios", s);
      setEditScenario(null);
      loadScenarios();
      toast.success("Scenario saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await api.delete(`/roleplay/scenarios/${id}`);
      loadScenarios();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const repCount = messages.filter((m) => m.role === "rep").length;
  const isVoice = session?.mode === "voice";
  const sessionLangInfo = session
    ? (languages.find((l) => l.code === (session.language || "tr")) || languages[0])
    : null;
  const sessionLangLabel = sessionLangInfo?.name || "Turkish";
  const sessionVoice = sessionLangInfo?.voice || "tr-TR";

  // ============ Active session view ============
  if (session) {
    const persona = activeScenario || {};
    const personaName = persona.persona_name || persona.name || session.scenario_name;
    const buyerTalking = voiceStatus === "speaking" || voiceStatus === "thinking";
    const youTalking = voiceStatus === "listening";
    const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
    const statusText = {
      idle: "Connecting…", listening: "🎙️ Listening to you…",
      thinking: "💭 Buyer is thinking…", speaking: "🔊 Buyer is speaking…",
    }[voiceStatus] || "";

    // ---- Voice call screen (Hyperbound-style, dark) ----
    if (isVoice && !score && !scoring) {
      const cardBase = "w-60 sm:w-64 rounded-3xl bg-white/5 border border-white/10 backdrop-blur shadow-2xl p-8 flex flex-col items-center gap-3 ring-4 transition-all duration-300";
      return (
        <div className="-m-6 lg:-m-8 h-[calc(100vh-4rem)] flex flex-col text-white bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-950">
          <div className="flex-1 flex min-h-0">
            <div className="flex-1 flex flex-col items-center justify-center gap-8 p-6 min-w-0">
            <div className="flex items-stretch justify-center gap-4 md:gap-8 flex-wrap">
              {/* Buyer */}
              <div className={`${cardBase} ${buyerTalking ? "ring-orange-400/80 scale-[1.02]" : "ring-white/5"}`}>
                <PersonaAvatar name={personaName} size="h-24 w-24 text-2xl" />
                <div className="text-center min-w-0 w-full">
                  <div className="font-semibold flex items-center justify-center gap-1.5 min-w-0">
                    <span className="truncate">{personaName}</span>
                    <span className="text-[10px] font-semibold bg-teal-400/20 text-teal-300 rounded px-1 py-0.5 shrink-0">AI</span>
                  </div>
                  {persona.role && <div className="text-sm text-zinc-50/55 truncate">{persona.role}</div>}
                </div>
                <div className="flex gap-1.5">
                  <MetaBadge meta={DIFFICULTY_META[persona.difficulty || "medium"]} />
                  <MetaBadge meta={CALL_TYPE_META[persona.call_type]} />
                </div>
              </div>
              {/* You */}
              <div className={`${cardBase} justify-center ${youTalking ? "ring-sky-400/80 scale-[1.02]" : "ring-white/5"}`}>
                <div className="h-24 w-24 rounded-full bg-white/10 flex items-center justify-center">
                  <UserIcon className="h-10 w-10 text-zinc-50/40" />
                </div>
                <div className="font-semibold">You</div>
                <div className="text-xs text-zinc-50/45">{sessionLangLabel}</div>
              </div>
            </div>

            <div className="text-center text-sm text-zinc-50/70 min-h-[20px] font-medium">{statusText}</div>
            <VoicePanel session={session} setMessages={setMessages} voiceLang={sessionVoice}
                        sttMode={voiceCaps.stt} ttsMode={voiceCaps.tts} onStatus={setVoiceStatus} />
            </div>
            {/* transcript sidebar */}
            <aside className="hidden lg:flex w-80 shrink-0 flex-col border-l border-white/10 bg-black/20 backdrop-blur">
              <div className="px-4 py-3 text-sm font-medium text-zinc-50/70 border-b border-white/10">Transcript</div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {messages.length === 0 && (
                  <div className="text-xs text-zinc-50/40 text-center pt-8">The conversation will appear here…</div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "rep" ? "justify-end" : "justify-start"}`}>
                    <div className={`rounded-2xl px-3 py-2 max-w-[85%] text-sm whitespace-pre-wrap ${m.role === "rep" ? "bg-orange-500 text-white" : "bg-white/10 text-white/90"}`}>
                      {m.text || <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            </aside>
          </div>

          {/* bottom call bar */}
          <div className="bg-black/40 border-t border-white/10 py-4 px-6 flex items-center justify-center gap-5">
            <span className="font-mono text-lg tabular-nums text-zinc-50/90">{mmss}</span>
            <Button onClick={finishSession} disabled={repCount === 0}
                    className="bg-red-500 hover:bg-red-600 rounded-full px-6 text-white">
              <PhoneOff className="h-4 w-4 mr-2" /> End Call
            </Button>
            <Button variant="ghost" className="text-zinc-50/70 hover:text-zinc-50 hover:bg-white/10" onClick={exitSession}>
              Exit
            </Button>
          </div>
        </div>
      );
    }

    // ---- Text chat / scoring / score ----
    return (
      <>
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                {isVoice ? <Mic className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
                {personaName}
                <Badge variant="outline">{sessionLangLabel}</Badge>
              </h1>
              <p className="text-sm text-muted-foreground">
                {isVoice ? "Voice call" : "Write to the buyer"} in {sessionLangLabel}. {repCount} messages.
              </p>
            </div>
            <div className="flex gap-2">
              {!score && !scoring && (
                <Button onClick={finishSession} disabled={repCount === 0} variant="destructive">
                  <Square className="h-4 w-4 mr-1" /> Bitir / End &amp; Score
                </Button>
              )}
              <Button variant="outline" onClick={exitSession}>Exit</Button>
            </div>
          </div>

          <Card>
            <CardContent className="pt-4">
              {messages.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-12">
                  Greet the buyer in {sessionLangLabel} to open the call 👋
                </div>
              )}
              <Transcript messages={messages} endRef={chatEndRef} />

              {!score && !scoring && !isVoice && (
                <div className="flex gap-2 mt-4">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                    }}
                    placeholder="Mesajını yaz... (Enter — send, Shift+Enter — new line)"
                    rows={2}
                    disabled={streaming}
                  />
                  <Button onClick={sendMessage} disabled={streaming || !draft.trim()} className="self-end">
                    {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {scoring && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                Görüşme analiz ediliyor — scoring the call...
              </CardContent>
            </Card>
          )}
          {score && <ScoreCard score={score} />}
        </div>
      </>
    );
  }

  // ============ Lobby: Training / Sessions / Progress ============
  return (
    <>
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="h-6 w-6" /> Spinhouse Roleplay
          </h1>
          <p className="text-sm text-muted-foreground">
            Training zone: AI buyer + automatic call review against the rubric.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="training"><MessageCircle className="h-4 w-4 mr-1" />Training</TabsTrigger>
            <TabsTrigger value="sessions"><History className="h-4 w-4 mr-1" />Sessions</TabsTrigger>
            <TabsTrigger value="progress"><TrendingUp className="h-4 w-4 mr-1" />Progress</TabsTrigger>
          </TabsList>

          <TabsContent value="training" className="space-y-3">
            {canManage && <VoicePicker />}
            <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(250px,1fr))]">
              {scenarios.map((s) => (
                <Card key={s.id} className="group flex flex-col min-w-0 p-4 gap-3 transition-colors hover:border-orange-500/60">
                  <div className="flex items-start gap-3 min-w-0">
                    <PersonaAvatar name={s.persona_name || s.name} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-1">
                        <h3 className="text-sm font-semibold leading-tight truncate">{s.persona_name || s.name}</h3>
                        {canManage && (
                          <span className="flex gap-0.5 shrink-0 -mt-1 -mr-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                                    onClick={() => setEditScenario({ ...s })}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                                    onClick={() => deleteScenario(s.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        )}
                      </div>
                      {s.role && <p className="text-xs text-muted-foreground truncate">{s.role}</p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <MetaBadge meta={CALL_TYPE_META[s.call_type]} />
                    <MetaBadge meta={DIFFICULTY_META[s.difficulty || "medium"]} />
                  </div>
                  {/* Description spoils the exercise — it telegraphs the buyer's attitude/objections.
                      Only show it to admins (their own config note); trainees must uncover the client themselves. */}
                  {canManage && s.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{s.description}</p>
                  )}
                  <div className="mt-auto space-y-2">
                    <Button className="w-full" onClick={() => startSession(s, "voice")}>
                      <Mic className="h-4 w-4 mr-1.5 shrink-0" /> Voice call
                    </Button>
                    <div className="flex items-center justify-between gap-2">
                      <Select value={getLang(s.id)} onValueChange={(v) => setCardLang((p) => ({ ...p, [s.id]: v }))}>
                        <SelectTrigger className="h-8 w-32 text-xs shrink-0 overflow-hidden [&>span]:min-w-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {languages.map((l) => (
                            <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground shrink-0"
                              onClick={() => startSession(s, "text")}>
                        <MessageCircle className="h-3.5 w-3.5 mr-1" /> Text
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            {canManage && (
              <Button variant="outline" onClick={() => { const nm = randomTurkishName(); setEditScenario({ name: nm, persona_name: nm, role: "", description: "", buyer_prompt: "", language: "tr", call_type: "cold", difficulty: "medium", traits: {} }); }}>
                <Plus className="h-4 w-4 mr-1" /> New scenario
              </Button>
            )}
          </TabsContent>

          <TabsContent value="sessions">
            <Card>
              <CardContent className="pt-4">
                {canViewAll && (
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <ViewToggle viewAll={viewAll} setViewAll={(v) => { setViewAll(v); setRepFilter("all"); }} />
                    {viewAll && (
                      <Select value={repFilter} onValueChange={setRepFilter}>
                        <SelectTrigger className="h-8 w-52 text-xs"><SelectValue placeholder="All reps" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All reps</SelectItem>
                          {[...new Set(pastSessions.map((s) => s.user_name).filter(Boolean))].sort().map((n) => (
                            <SelectItem key={n} value={n}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                {pastSessions.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    {viewAll ? "No sessions from any rep yet." : "No sessions yet — start your first one in Training."}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="py-2 pr-2">Date</th>
                        {viewAll && <th className="py-2 pr-2">Rep</th>}
                        <th className="py-2 pr-2">Persona</th>
                        <th className="py-2 pr-2">Type · Level</th>
                        <th className="py-2 pr-2">Mode</th>
                        <th className="py-2 pr-2 text-right">Score</th>
                        <th className="py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewAll && repFilter !== "all" ? pastSessions.filter((s) => s.user_name === repFilter) : pastSessions).map((s) => (
                        <tr key={s.id} className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                            onClick={() => openPastSession(s.id)}>
                          <td className="py-2 pr-2 whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()} {new Date(s.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                          {viewAll && <td className="py-2 pr-2 font-medium whitespace-nowrap">{s.user_name || "—"}</td>}
                          <td className="py-2 pr-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <PersonaAvatar name={s.persona_name || s.scenario_name} size="h-7 w-7" />
                              <div className="min-w-0">
                                <div className="font-medium truncate">{s.persona_name || s.scenario_name}</div>
                                {s.persona_role && <div className="text-xs text-muted-foreground truncate">{s.persona_role}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="py-2 pr-2">
                            <div className="flex flex-wrap gap-1">
                              <MetaBadge meta={CALL_TYPE_META[s.call_type]} />
                              <MetaBadge meta={DIFFICULTY_META[s.difficulty || "medium"]} />
                            </div>
                          </td>
                          <td className="py-2 pr-2">
                            {s.mode === "voice"
                              ? <span className="inline-flex items-center gap-1"><Mic className="h-3 w-3" />Voice</span>
                              : <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" />Text</span>}
                          </td>
                          <td className={`py-2 pr-2 text-right font-bold tabular-nums ${s.score ? scoreColor(s.score.overall) : "text-muted-foreground"}`}>
                            {s.score ? s.score.overall : <span className="text-xs font-normal">{s.status}</span>}
                          </td>
                          <td className="py-2 text-right">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="progress">
            <Card>
              <CardContent className="pt-4 space-y-4">
                {canViewAll && (
                  <ViewToggle viewAll={viewAll} setViewAll={(v) => { setViewAll(v); setRepFilter("all"); }} />
                )}

                {viewAll ? (
                  !progress?.reps || progress.reps.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">No scored sessions from any rep yet.</div>
                  ) : (
                    <div>
                      <div className="text-sm font-semibold mb-2">Reps leaderboard</div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b">
                            <th className="py-2 pr-2">Rep</th>
                            <th className="py-2 pr-2 text-right">Sessions</th>
                            <th className="py-2 pr-2 text-right">Avg score</th>
                            <th className="py-2 pr-2 whitespace-nowrap">Last active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {progress.reps.map((r) => (
                            <tr key={r.user_id} className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                                onClick={() => { setRepFilter(r.user_name); setTab("sessions"); }}>
                              <td className="py-2 pr-2 font-medium">{r.user_name || "—"}</td>
                              <td className="py-2 pr-2 text-right tabular-nums">{r.sessions}</td>
                              <td className={`py-2 pr-2 text-right font-bold tabular-nums ${scoreColor(r.avg_overall)}`}>{r.avg_overall}</td>
                              <td className="py-2 pr-2 whitespace-nowrap text-muted-foreground">{r.last_at ? new Date(r.last_at).toLocaleDateString() : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="text-xs text-muted-foreground mt-2">Click a rep to open their sessions.</div>
                    </div>
                  )
                ) : (!progress || progress.points.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    The chart appears after your first scored session.
                  </div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={progress.points.map((p, i) => ({ ...p, idx: i + 1 }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="idx" label={{ value: "Session #", position: "insideBottom", offset: -2 }} />
                        <YAxis domain={[0, 100]} />
                        <Tooltip
                          formatter={(v) => [v, "Score"]}
                          labelFormatter={(idx) => {
                            const p = progress.points[idx - 1];
                            return p ? `${p.date} — ${p.scenario}` : idx;
                          }}
                        />
                        <ReferenceLine y={75} stroke="#10b981" strokeDasharray="4 4" />
                        <Line type="monotone" dataKey="overall" stroke="#f97316" strokeWidth={2} dot />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Past session viewer */}
      <Dialog open={!!viewSession} onOpenChange={(o) => !o && setViewSession(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewSession?.scenario_name} — {viewSession && new Date(viewSession.created_at).toLocaleString()}</DialogTitle>
          </DialogHeader>
          {viewSession?.score && <ScoreCard score={viewSession.score} compact />}
          <div className="space-y-2 mt-2">
            {(viewSession?.transcript || []).map((m, i) => (
              <div key={i} className={`flex ${m.role === "rep" ? "justify-end" : "justify-start"}`}>
                <div className={`rounded-lg px-3 py-2 max-w-[80%] whitespace-pre-wrap text-sm ${
                  m.role === "rep" ? "bg-orange-500 text-white" : "bg-muted"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Scenario editor (admin) */}
      <Dialog open={!!editScenario} onOpenChange={(o) => !o && setEditScenario(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editScenario?.id ? "Edit scenario" : "New scenario"}</DialogTitle>
          </DialogHeader>
          {editScenario && (
            <div className="space-y-3">
              <div>
                <Label>Persona name (auto-generated)</Label>
                <div className="flex gap-2">
                  <Input value={editScenario.persona_name || ""} placeholder="e.g. Muhammed Gürbüz"
                         onChange={(e) => setEditScenario({ ...editScenario, persona_name: e.target.value, name: e.target.value })} />
                  <Button type="button" variant="outline" size="icon" className="shrink-0" title="New random name"
                          onClick={() => { const nm = randomTurkishName(); setEditScenario({ ...editScenario, persona_name: nm, name: nm }); }}>
                    🎲
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Call type</Label>
                  <Select value={editScenario.call_type || "cold"}
                          onValueChange={(v) => setEditScenario({ ...editScenario, call_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CALL_TYPE_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <Select value={editScenario.difficulty || "medium"}
                          onValueChange={(v) => setEditScenario({ ...editScenario, difficulty: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(DIFFICULTY_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* persona builder — pick chips, the prompt is composed server-side */}
              {traitDefs?.traits?.length > 0 && !(editScenario.buyer_prompt || "").trim() && (
                <div className="space-y-3 rounded-lg border p-3">
                  <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Build the persona — pick traits, we compose the character
                  </div>
                  {traitDefs.traits.map((cat) => (
                    <TraitRow
                      key={cat.key}
                      category={cat}
                      value={editScenario.traits?.[cat.key]}
                      onChange={(v) => {
                        const next = { ...editScenario, traits: { ...(editScenario.traits || {}), [cat.key]: v } };
                        if (cat.key === "role") {
                          const opt = cat.options.find((o) => o.value === v);
                          next.role = opt ? opt.label : "";
                        }
                        setEditScenario(next);
                      }}
                    />
                  ))}
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Input value={editScenario.description || ""}
                       onChange={(e) => setEditScenario({ ...editScenario, description: e.target.value })} />
              </div>
              <details className="rounded-lg border p-3" open={!!(editScenario.buyer_prompt || "").trim()}>
                <summary className="text-xs font-medium text-muted-foreground cursor-pointer">
                  Advanced — free-hand buyer prompt (overrides the trait builder)
                </summary>
                <Textarea rows={12} className="font-mono text-xs mt-2"
                          placeholder="Leave empty to build from traits above, or write a full persona prompt here."
                          value={editScenario.buyer_prompt || ""}
                          onChange={(e) => setEditScenario({ ...editScenario, buyer_prompt: e.target.value })} />
              </details>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditScenario(null)}>Cancel</Button>
            <Button onClick={saveScenario}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
